import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, FileText, Download, FolderGit2, Calendar, ChevronRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { LoadingSpinner } from '../components/common';
import { Navbar } from '../components/Navbar';
import Footer from '../components/Footer';
import { AnalysisService } from '../services/analysisService';
import { vibeMapperAnalysis, vibeMapperMaintenanceScore } from '../components/data/dummyData';
import {
  fetchDriftReport,
  generateDriftDocument,
  vibeMapperDriftReport,
} from '../components/analysis/drift';
import type { MaintenanceScoreResult } from '../components/types/analysis.types';
import { useAppContext } from '../contexts/useAppContext';
import { useJWTAuth } from '../contexts/useJWTAuth';

interface DocumentData {
  title: string;
  fileName: string;
  content: string;
}

interface RepoSummary {
  name: string;
  description?: string;
  keyTechnologies?: string[];
  scanDate?: string;
  projectPath?: string;
  projectDescription?: string;
  projectName?: string;
}

export const DocumentViewPage: React.FC = () => {
  const navigate = useNavigate();
  const { repoId, jobId, docIndex } = useParams<{
    repoId: string;
    jobId: string;
    docIndex: string;
  }>();
  const [searchParams] = useSearchParams();
  const { isDemoMode: isDemoContext } = useAppContext();
  const isDemoMode = isDemoContext || searchParams.get('demo') === 'true';
  const { isAuthenticated, isInitialized } = useJWTAuth();

  useEffect(() => {
    if (isInitialized && !isAuthenticated && !isDemoMode) {
      sessionStorage.setItem('auth_return_url', window.location.pathname);
      navigate('/signin', { replace: true });
    }
  }, [isInitialized, isAuthenticated, isDemoMode, navigate]);
  const isDriftDocument = searchParams.get('type') === 'drift';
  const projectPath = searchParams.get('project') || undefined;
  // Get repo context from URL params for drift document generation
  const repoName = searchParams.get('repoName') || undefined;
  const repoLanguage = searchParams.get('language') || undefined;

  const [document, setDocument] = useState<DocumentData | null>(null);
  const [repoSummary, setRepoSummary] = useState<RepoSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!jobId || docIndex === undefined) {
      setError('Missing job ID or document index');
      setLoading(false);
      return;
    }

    const docIdx = parseInt(docIndex, 10);
    if (isNaN(docIdx)) {
      setError('Invalid document index');
      setLoading(false);
      return;
    }

    if (isDemoMode) {
      // Handle drift documents for demo - use mock drift data with 6-pillar maintenance score
      if (isDriftDocument) {
        const driftDoc = generateDriftDocument(vibeMapperDriftReport, vibeMapperMaintenanceScore, {
          repositoryName: repoName || vibeMapperDriftReport.repositoryName,
          primaryLanguage: repoLanguage || vibeMapperDriftReport.primaryLanguage,
        });
        setDocument(driftDoc);
        setRepoSummary({
          name: vibeMapperDriftReport.repositoryName,
          description: undefined,
          keyTechnologies: undefined,
          scanDate: vibeMapperDriftReport.analysisDate,
        });
        setLoading(false);
        return;
      }

      // Use dummy data for other demo documents
      const docs = vibeMapperAnalysis.documents;
      if (docs && docs[docIdx]) {
        setDocument(docs[docIdx]);
        setRepoSummary({
          name: vibeMapperAnalysis.repositoryName,
          description: vibeMapperAnalysis.repositoryDetails?.description,
          keyTechnologies: vibeMapperAnalysis.repositoryDetails?.keyTechnologies,
          scanDate: vibeMapperAnalysis.scanDate,
        });
      } else {
        setError('Document not found');
      }
      setLoading(false);
      return;
    }

    // Handle drift documents specially - fetch from drift API
    if (isDriftDocument && repoId) {
      const fetchDriftDocument = async () => {
        try {
          // Try to get maintenanceScore from the job result if available
          let maintenanceScore: MaintenanceScoreResult | null = null;
          if (jobId && !isDemoMode) {
            try {
              const jobRes = await AnalysisService.getJobStatus(jobId);
              if (jobRes?.data?.status === 'completed' && jobRes?.data?.result?.maintenanceScore) {
                maintenanceScore = jobRes.data.result.maintenanceScore;
              }
            } catch {
              // Ignore errors fetching job - we'll fall back to drift-only doc
            }
          }

          const driftReport = await fetchDriftReport(repoId, repoName, undefined, repoLanguage);
          if (driftReport) {
            const driftDoc = generateDriftDocument(driftReport, maintenanceScore, {
              repositoryName: repoName,
              primaryLanguage: repoLanguage,
            });
            setDocument(driftDoc);
            setRepoSummary({
              name: driftReport.repositoryName,
              description: undefined,
              keyTechnologies: undefined,
              scanDate: maintenanceScore?.collectedAt || driftReport.analysisDate,
            });
          } else {
            setError('Drift report not found');
          }
        } catch (e: unknown) {
          setError(e instanceof Error ? e.message : 'Failed to load drift document');
        } finally {
          setLoading(false);
        }
      };
      fetchDriftDocument();
      return;
    }

    // Fetch real job data
    const fetchDocument = async () => {
      try {
        const res = await AnalysisService.getJobStatus(jobId);
        if (res?.data?.status === 'completed' && res?.data?.result) {
          const result = res.data.result;
          const project = projectPath
            ? (result.projects ?? []).find(
                (p: { projectPath?: string }) => p.projectPath === projectPath,
              )
            : null;
          const docs = project ? project.documents : result.documents;
          if (docs && docs[docIdx]) {
            setDocument(docs[docIdx]);
            setRepoSummary({
              name: result.repositoryName,
              description: result.repositoryDetails?.description,
              keyTechnologies: project
                ? project.repositoryDetails?.keyTechnologies
                : result.repositoryDetails?.keyTechnologies,
              scanDate: result.scanDate,
              projectPath: project?.projectPath,
              projectDescription: project?.projectDescription,
              projectName: project?.repositoryName,
            });
          } else {
            setError('Document not found');
          }
        } else if (res?.data?.status === 'failed') {
          setError('Analysis failed');
        } else {
          setError('Analysis not yet complete');
        }
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Failed to load document');
      } finally {
        setLoading(false);
      }
    };

    fetchDocument();
  }, [jobId, docIndex, isDriftDocument, repoId, isDemoMode, projectPath, repoLanguage, repoName]);

  const handleDownload = () => {
    if (!document) return;
    const blob = new Blob([document.content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = window.document.createElement('a');
    a.href = url;
    a.download = document.fileName;
    window.document.body.appendChild(a);
    a.click();
    window.document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !document) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error || 'Document not found'}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const docFileName = document.fileName.replace(/^ai-docs\//, '').replace(/^ai-/, '');

  return (
    <div className="min-h-screen flex flex-col transition-colors duration-200">
      <Navbar pageTitle={document.title} showDivider={true} />

      <div className="flex-1 px-4 sm:px-6 lg:px-8 py-8 pt-24">
        <div className="max-w-7xl mx-auto w-full">
          {/* Back + actions row */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => navigate(`/jobs/${jobId}/results`)}
              className="inline-flex items-center gap-2 text-secondary hover:text-primary transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Analysis
            </button>
            <button
              onClick={handleDownload}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" />
              Download
            </button>
          </div>

          {/* Document header */}
          <div className="mb-6">
            {/* Breadcrumb */}
            <div className="flex items-center gap-1.5 text-sm text-secondary mb-3 flex-wrap">
              {repoSummary && <span>{repoSummary.name}</span>}
              {repoSummary?.projectPath && (
                <>
                  <ChevronRight className="w-3.5 h-3.5 text-tertiary shrink-0" />
                  <span className="font-mono text-xs">{repoSummary.projectPath}</span>
                </>
              )}
              <ChevronRight className="w-3.5 h-3.5 text-tertiary shrink-0" />
              <span className="font-mono text-xs">{docFileName}</span>
            </div>

            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <FileText className="w-6 h-6 text-teal-500 shrink-0" />
                <h1 className="text-2xl font-bold text-primary">{document.title}</h1>
              </div>
              {repoSummary?.scanDate && (
                <span className="flex items-center gap-1.5 text-sm text-secondary shrink-0">
                  <Calendar className="w-4 h-4" />
                  {repoSummary.scanDate}
                </span>
              )}
            </div>

            {repoSummary?.description && (
              <p className="mt-2 text-sm text-secondary leading-relaxed">
                {repoSummary.description}
              </p>
            )}

            {/* Project + description */}
            {repoSummary?.projectPath && (
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-teal-50 dark:bg-teal-900/30 border border-teal-200 dark:border-teal-800 text-xs font-mono text-teal-700 dark:text-teal-300">
                  <FolderGit2 className="w-3 h-3" />
                  {repoSummary.projectPath}
                </span>
                {repoSummary.projectName && repoSummary.projectName !== repoSummary.name && (
                  <span className="text-sm font-medium text-primary">
                    {repoSummary.projectName}
                  </span>
                )}
                {repoSummary.projectDescription && (
                  <span className="text-sm text-secondary">{repoSummary.projectDescription}</span>
                )}
              </div>
            )}

            {/* Tech tags */}
            {repoSummary?.keyTechnologies && repoSummary.keyTechnologies.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {repoSummary.keyTechnologies.slice(0, 6).map((tech, i) => (
                  <span
                    key={i}
                    className="px-2 py-0.5 text-xs font-medium rounded-full bg-slate-100 dark:bg-slate-700 text-secondary"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Document content */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-slate-700/60 shadow-xs px-8 py-8">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ children }) => (
                  <h1 className="text-2xl font-bold mt-8 mb-3 text-primary pb-2 border-b border-slate-200 dark:border-slate-700/60 first:mt-0">
                    {children}
                  </h1>
                ),
                h2: ({ children }) => (
                  <h2 className="text-lg font-semibold mt-7 mb-2 text-primary">{children}</h2>
                ),
                h3: ({ children }) => (
                  <h3 className="text-base font-semibold mt-5 mb-1.5 text-primary">{children}</h3>
                ),
                h4: ({ children }) => (
                  <h4 className="text-sm font-semibold mt-4 mb-1 text-primary uppercase tracking-wide">
                    {children}
                  </h4>
                ),
                p: ({ children }) => <p className="mb-4 text-secondary leading-7">{children}</p>,
                ul: ({ children }) => (
                  <ul className="list-disc pl-6 mb-4 text-secondary space-y-1.5">{children}</ul>
                ),
                ol: ({ children }) => (
                  <ol className="list-decimal pl-6 mb-4 text-secondary space-y-1.5">{children}</ol>
                ),
                li: ({ children }) => <li className="leading-relaxed">{children}</li>,
                code: ({ className, children }) => {
                  const isInline = !className;
                  return isInline ? (
                    <code className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700/70 rounded text-[0.85em] font-mono text-teal-700 dark:text-teal-300">
                      {children}
                    </code>
                  ) : (
                    <code className="block p-4 bg-slate-900 dark:bg-slate-950 text-emerald-400 rounded-lg text-sm font-mono overflow-x-auto">
                      {children}
                    </code>
                  );
                },
                pre: ({ children }) => (
                  <pre className="mb-4 rounded-lg overflow-hidden">{children}</pre>
                ),
                a: ({ href, children }) => (
                  <a
                    href={href}
                    className="text-teal-600 dark:text-teal-400 hover:underline underline-offset-2"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {children}
                  </a>
                ),
                blockquote: ({ children }) => (
                  <blockquote className="border-l-4 border-teal-400 dark:border-teal-600 pl-4 py-0.5 my-4 bg-teal-50/50 dark:bg-teal-900/10 rounded-r-md text-secondary italic">
                    {children}
                  </blockquote>
                ),
                hr: () => <hr className="my-6 border-slate-200 dark:border-slate-700/60" />,
                table: ({ children }) => (
                  <div className="overflow-x-auto mb-6 rounded-lg border border-slate-200 dark:border-slate-700">
                    <table className="min-w-full border-collapse text-sm">{children}</table>
                  </div>
                ),
                thead: ({ children }) => (
                  <thead className="bg-slate-50 dark:bg-slate-700/60">{children}</thead>
                ),
                tbody: ({ children }) => (
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-700 bg-white dark:bg-gray-800">
                    {children}
                  </tbody>
                ),
                tr: ({ children }) => (
                  <tr className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                    {children}
                  </tr>
                ),
                th: ({ children }) => (
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-primary uppercase tracking-wide border-r border-slate-200 dark:border-slate-700 last:border-r-0">
                    {children}
                  </th>
                ),
                td: ({ children }) => (
                  <td className="px-4 py-2.5 text-secondary border-r border-slate-200 dark:border-slate-700 last:border-r-0">
                    {children}
                  </td>
                ),
              }}
            >
              {document.content}
            </ReactMarkdown>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};
